// This file is mostly unchanged from the mobile repo
// differences are:
// 1. useStore is used to get the patient id
// 2. RxToast is removed
// 3. session_type is set to 'clinician' for api calls that do not require a specific session id
// mobile repo commit: aaf81442825d53639430623a62bbaf7b19ed1411

import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sampleSize } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useImmer } from 'use-immer';
import { advisorChatApi } from '../api';
import {
  AdvisorChatMessageResponse,
  AdvisorChatSessionResponse,
  AdvisorChatSessionSummaryResponse,
  AdvisorChatSubjectTypeEnum,
} from '../api/generated/models';
// import { RxToast } from '../components/Toast';
import { useStore } from '../context';
import { TFunc, useTranslation } from '../i18n';
import { fastGetToday } from '../utils/dateUtils';

const DEFAULT_QUERY_STALE_TIME = 1000 * 60 * 15; // 15 minutes

export type ChatContext = {
  filterDatesLte?: string | null,
  filterDatesGte?: string | null,
};

export type ChatMessage = {
  id: number,
  sessionId: number,
  timestamp: string,
  role: 'user' | 'assistant',
  content: string,
  contentType: 'text/markdown' | 'text/plain',
  isOptimistic?: boolean,
};

export type ChatSession = {
  id: number,
  userId: number,
  patientId: number,
  subjectType?: string | null,
  subjectId?: string | null,
  title: string,
  messages: ChatMessage[],
  createdTime: string,
  updatedTime: string,
  hasMoreMessages?: boolean,
  nextTimestamp?: string | null,
};

const transformMessage = (data: AdvisorChatMessageResponse): ChatMessage => ({
  id: data.id,
  sessionId: data.session_id,
  timestamp: data.timestamp,
  role: data.role as 'user' | 'assistant',
  content: data.content || data.title || '',
  contentType: data.content_type as 'text/markdown' | 'text/plain',
});

const transformSession = (data: AdvisorChatSessionResponse): ChatSession => ({
  id: data.id,
  userId: data.user_id,
  patientId: data.patient_id,
  subjectType: data.subject_type,
  subjectId: data.subject_id,
  title: data.title || '',
  messages: data.messages?.rows?.map(transformMessage) || [],
  createdTime: data.created_time,
  updatedTime: data.updated_time,
  hasMoreMessages: data.messages?.has_more || false,
  nextTimestamp: data.messages?.message_next_timestamp,
});

const chatContextToDict = (context?: ChatContext) => {
  if (!context) {
    return null;
  }
  return {
    filter_dates_lte: context.filterDatesLte,
    filter_dates_gte: context.filterDatesGte,
  };
};

const chatService = {
  async createSession(
    opts: {
      patientId: number,
      initialMessage: string,
      context?: ChatContext,
    } & AdvisorChatSubject,
  ) {
    const response = await advisorChatApi.appApiAdvisorChatApiCreatePatientAdvisorChatSession({
      patient_id: opts.patientId,
      session_type: 'clinician',
      AdvisorChatMessageRequest: {
        content: opts.initialMessage,
        subject_type: opts.subjectType,
        subject_id: opts.subjectId,
        context_options: chatContextToDict(opts.context),
      },
    });
    return transformMessage(response.data);
  },

  async sendMessage(opts: {
    patientId: number,
    sessionId: number,
    content: string,
  }) {
    const response = await advisorChatApi.appApiAdvisorChatApiPostPatientAdvisorChatMessage({
      patient_id: opts.patientId,
      advisor_chat_session_id: opts.sessionId,
      AdvisorChatMessageRequest: { content: opts.content },
    });
    return transformMessage(response.data);
  },

  async getSession(opts: {
    patientId: number,
    sessionId: number,
    messageLimit?: number,
    messageNextTimestamp?: string,
  }) {
    const response = await advisorChatApi.appApiAdvisorChatApiGetPatientAdvisorChatSession({
      patient_id: opts.patientId,
      advisor_chat_session_id: opts.sessionId,
      message_limit: opts.messageLimit,
      message_next_timestamp: opts.messageNextTimestamp,
    });
    return transformSession(response.data);
  },

  async getSessionBySubject(opts: {
    patientId: number,
    subjectType: string,
    subjectId: string,
    messageLimit?: number,
    messageNextTimestamp?: string,
  }) {
    const response = await advisorChatApi.appApiAdvisorChatApiGetPatientAdvisorChatSessionBySubject({
      patient_id: opts.patientId,
      subject_type: opts.subjectType,
      subject_id: opts.subjectId,
      message_limit: opts.messageLimit,
      message_next_timestamp: opts.messageNextTimestamp,
      session_type: 'clinician',
    });
    return transformSession(response.data);
  },

  async getSessions(opts: {
    patientId: number,
    limit?: number,
    nextTimestamp?: string,
    subjectType?: AdvisorChatSubjectTypeEnum,
  }) {
    const response = await advisorChatApi.appApiAdvisorChatApiGetPatientAdvisorChatSessions({
      patient_id: opts.patientId,
      subject_type: opts.subjectType,
      limit: opts.limit,
      next_timestamp: opts.nextTimestamp,
      session_type: 'clinician',
    });
    return response.data;
  },
};

export type AdvisorChatSubject = {
  subjectType: AdvisorChatSubjectTypeEnum,
  subjectId?: string,
};

export const useAdvisorChat = (
  opts: {
    sessionId?: number,
  } & AdvisorChatSubject,
) => {
  const { patient } = useStore();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useImmer<ChatMessage[] | undefined>(undefined);
  const [paginationState, setPaginationState] = useImmer({
    hasMoreMessages: false,
    nextTimestamp: '',
  });
  const [llmContext, setLlmContext] = useImmer<ChatContext | undefined>(undefined);
  const [sendError, setSendError] = useState<Error | null>(null);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);

  const sessionQuery = useQuery({
    queryKey: ['chat-session', opts.sessionId, opts.subjectType, opts.subjectId],
    queryFn: async () => {
      if (opts.sessionId) {
        return chatService.getSession({
          patientId: patient!.patient_id,
          sessionId: opts.sessionId,
        });
      }
      if (opts.subjectType && opts.subjectId) {
        return chatService.getSessionBySubject({
          patientId: patient!.patient_id,
          subjectType: opts.subjectType,
          subjectId: opts.subjectId,
        });
      }
      return null;
    },
    enabled: !!patient?.patient_id && (!!opts.sessionId || (!!opts.subjectType && !!opts.subjectId)),
  });

  useEffect(() => {
    if (!sessionQuery.data) {
      return;
    }
    // We already called setMessages in the sendMessageMutation onSuccess
    // having this here deletes the previous messages if session id changes
    // setMessages(sessionQuery.data.messages);
    setPaginationState(draft => {
      draft.hasMoreMessages = sessionQuery.data?.hasMoreMessages || false;
      draft.nextTimestamp = sessionQuery.data?.nextTimestamp || '';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionQuery.data]);

  const loadMoreMessagesMutation = useMutation({
    mutationFn: async () => {
      if (!paginationState.hasMoreMessages || !paginationState.nextTimestamp || !sessionQuery.data?.id) {
        return;
      }
      let moreData: ChatSession | null = null;
      if (opts.sessionId) {
        moreData = await chatService.getSession({
          patientId: patient!.patient_id,
          sessionId: opts.sessionId,
          messageLimit: 10,
          messageNextTimestamp: paginationState.nextTimestamp,
        });
      } else if (opts.subjectType && opts.subjectId) {
        moreData = await chatService.getSessionBySubject({
          patientId: patient!.patient_id,
          subjectType: opts.subjectType,
          subjectId: opts.subjectId,
          messageLimit: 10,
          messageNextTimestamp: paginationState.nextTimestamp,
        });
      }
      return moreData;
    },
    onSuccess: (moreData) => {
      if (moreData && moreData.messages.length > 0) {
        setMessages(draft => {
          if (!draft) {
            return moreData.messages;
          }
          draft.push(...moreData.messages);
        });
        setPaginationState(draft => {
          draft.hasMoreMessages = moreData.hasMoreMessages || false;
          draft.nextTimestamp = moreData.nextTimestamp || '';
        });
      }
    },
    onError: (error) => {
      console.error('Failed to load more messages:', error);
      Sentry.captureException(error);
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      setSendError(null);
      const date = new Date();
      const userMessage: ChatMessage = {
        id: date.getTime(),
        sessionId: sessionQuery.data?.id || 0,
        timestamp: date.toISOString(),
        role: 'user',
        content,
        contentType: 'text/plain',
        isOptimistic: true,
      };
      setMessages(draft => {
        if (!draft) {
          return [userMessage];
        }
        draft.unshift(userMessage);
      });
      if (sessionQuery.data?.id) {
        const response = await chatService.sendMessage({
          patientId: patient!.patient_id,
          sessionId: sessionQuery.data.id,
          content,
        });
        return response;
      }
      const response = await chatService.createSession({
        patientId: patient!.patient_id,
        initialMessage: content,
        subjectType: opts.subjectType,
        subjectId: opts.subjectId,
        context: llmContext,
      });
      return response;
    },
    onSuccess: (assistantMessage) => {
      setLastFailedMessage(null);
      setMessages(draft => {
        if (!draft) {
          return [assistantMessage];
        }
        draft.unshift(assistantMessage);
      });
      if (!sessionQuery.data?.id) {
        sessionQuery.refetch();
        queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      }
    },
    onError: (error, content) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      Sentry.captureException(error, {
        tags: {
          feature: 'rxassistant_chat',
          error_status: status?.toString() ?? 'unknown',
        },
        extra: {
          sessionId: sessionQuery.data?.id,
          subjectType: opts.subjectType,
        },
      });
      setSendError(error as Error);
      setLastFailedMessage(content);
      setMessages(draft => {
        const optimisticMessageIndex = (draft || []).findLastIndex(message => message.isOptimistic);
        if (optimisticMessageIndex !== -1) {
          (draft || []).splice(0, optimisticMessageIndex + 1);
        }
      });
    },
  });

  const clearSendError = useCallback(() => {
    setSendError(null);
    setLastFailedMessage(null);
  }, []);

  const retryLastMessage = useCallback(() => {
    if (!lastFailedMessage) {
      return Promise.resolve(undefined);
    }
    return sendMessageMutation.mutateAsync(lastFailedMessage);
  }, [lastFailedMessage, sendMessageMutation]);

  return {
    isSessionLoading: (opts.sessionId || (opts.subjectType && opts.subjectId)) ? sessionQuery.isLoading : false,
    sessionError: (sessionQuery.error as any)?.message !== 'Advisor chat session not found' ? sessionQuery.error : null,
    session: sessionQuery.data,
    messages,
    sendMessageMutation,
    refetch: sessionQuery.refetch,
    loadMoreMessages: loadMoreMessagesMutation.mutateAsync,
    hasMoreMessages: paginationState.hasMoreMessages,
    isLoadingMore: loadMoreMessagesMutation.isLoading,
    sendMessage: sendMessageMutation.mutateAsync,
    sendError,
    clearSendError,
    retryLastMessage,
    llmContext,
    setLlmContext,
  };
};

export const useAdvisorChatSessions = (opts?: {
  subjectType?: AdvisorChatSubjectTypeEnum,
}) => {
  const { patient } = useStore();
  const { t } = useTranslation();
  const [sessions, setSessions] = useImmer<AdvisorChatSessionSummaryResponse[]>([]);
  const [paginationState, setPaginationState] = useImmer({
    hasMoreSessions: false,
    nextTimestamp: '',
  });

  const sessionListQuery = useQuery({
    queryKey: ['chat-sessions'],
    queryFn: () =>
      chatService.getSessions({
        patientId: patient!.patient_id,
        subjectType: opts?.subjectType,
      }),
    enabled: !!patient?.patient_id,
  });

  useEffect(() => {
    if (!sessionListQuery.data) {
      return;
    }
    setSessions(sessionListQuery.data?.rows || []);
    setPaginationState(draft => {
      draft.hasMoreSessions = sessionListQuery.data?.has_more || false;
      draft.nextTimestamp = sessionListQuery.data?.next_timestamp || '';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionListQuery.data]);

  const loadMoreSessionsMutation = useMutation({
    mutationFn: async () => {
      if (!paginationState.hasMoreSessions || !paginationState.nextTimestamp) {
        return;
      }
      const moreData = await chatService.getSessions({
        patientId: patient!.patient_id,
        limit: 20,
        nextTimestamp: paginationState.nextTimestamp,
        subjectType: opts?.subjectType,
      });
      return moreData;
    },
    onSuccess: (moreData) => {
      if (moreData && moreData.rows && moreData.rows.length > 0) {
        setSessions(draft => {
          draft.push(...moreData.rows);
        });
        setPaginationState(draft => {
          draft.hasMoreSessions = moreData.has_more || false;
          draft.nextTimestamp = moreData.next_timestamp || '';
        });
      }
    },
    onError: (error) => {
      console.error('Failed to load more sessions:', error);
      Sentry.captureException(error);
    },
  });

  return {
    ...sessionListQuery,
    data: sessions,
    hasMoreSessions: paginationState.hasMoreSessions,
    isLoadingMore: loadMoreSessionsMutation.isLoading,
    loadMoreSessions: loadMoreSessionsMutation.mutateAsync,
  };
};

export const useAdvisorCommonQuestions = (opts: AdvisorChatSubject) => {
  const { t } = useTranslation();
  const { patient } = useStore();
  const today = fastGetToday();

  const advisorCommonQuestionsQuery = useQuery({
    queryKey: ['advisor-common-questions', today, opts.subjectType, opts.subjectId],
    queryFn: () => {
      const response = advisorChatApi.appApiAdvisorChatApiGetAdvisorQuestionSuggestions({
        patient_id: patient!.patient_id,
        question_date: today,
        subject_type: opts.subjectType,
        subject_id: opts.subjectId,
      });
      return response;
    },
    staleTime: DEFAULT_QUERY_STALE_TIME,
  });

  const allQuestions = useMemo(() => {
    const questions = advisorCommonQuestionsQuery.data?.data?.map((q, i) => ({
      id: q.id,
      question: q.question,
    })) || [{ id: -1, question: getAdvisorCommonQuestions(t, 1)[0].questionTr }];
    return sampleSize(questions, 4).filter(q => q.id && q.question);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisorCommonQuestionsQuery.data]);

  return {
    query: advisorCommonQuestionsQuery,
    data: allQuestions,
  };
};

export const getAdvisorCommonQuestions = (t: TFunc, count?: number) => {
  const questions = [
    {
      id: 'how-can-i-spend-less-on-food',
      questionTr: t('How can I spend less on food?'),
    },
    {
      id: 'how-can-i-improve-my-heart-health',
      questionTr: t('How can I improve my heart health?'),
    },
    {
      id: 'how-can-i-improve-my-lunch-choices',
      questionTr: t('How can I improve my lunch choices?'),
    },
    {
      id: 'did-i-meet-my-protein-goal-today',
      questionTr: t('Did I meet my protein goal today?'),
    },
    {
      id: 'when-do-i-have-the-most-calories',
      questionTr: t('When do I have the most calories?'),
    },
    {
      id: 'what-are-my-best-sources-of-protein',
      questionTr: t('What are my best sources of protein?'),
    },
    {
      id: 'how-can-i-add-vegetables-to-my-meals',
      questionTr: t('How can I add vegetables to my meals?'),
    },
    {
      id: 'what-is-a-high-fiber-dinner-for-me',
      questionTr: t("What's a high-fiber dinner for me?"),
    },
    {
      id: 'what-are-the-best-sources-of-calcium',
      questionTr: t('What are the best sources of calcium?'),
    },
    {
      id: 'what-should-i-have-for-dinner-today',
      questionTr: t('What should I have for dinner today?'),
    },
    {
      id: 'what-is-missing-from-my-breakfast',
      questionTr: t("What's missing from my breakfast?"),
    },
    {
      id: 'what-are-some-low-sugar-snacks',
      questionTr: t('What are some low-sugar snacks?'),
    },
  ];
  return sampleSize(questions, count || questions.length);
};

export const trimTitle = (title?: string | null) => {
  if (!title) {
    return '';
  }
  const trimmedFront = title.replace(/^[^\w\s]*\s*/, '');
  const match = trimmedFront.match(/^[^.!?]*[.!?]/);
  return match ? match[0] : trimmedFront || title;
};
